import {
  Chart,
  type ChartConfiguration,
  type ChartData,
  type ChartDataset,
  type Plugin,
} from 'chart.js';

import type { Scan } from '../../../api/generated/insights.schemas';
import '../../../utils/chartSetup';

const RADAR_PX = 720;
const LINE_W = 1100;
const LINE_H = 600;
const PRIMARY = 'rgba(166, 30, 105, 1)';
const PRIMARY_FILL = 'rgba(166, 30, 105, 0.25)';

// Paint a solid white background under the chart so the JPEG export is
// readable. Chart.js leaves the canvas transparent by default, and JPEG has
// no alpha channel — without this the exported image renders black where the
// chart didn't paint.
const whiteBackgroundPlugin: Plugin = {
  id: 'pdfWhiteBackground',
  beforeDraw: (chart) => {
    const { ctx, width, height } = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  },
};

function createOffscreenCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  // Keep it out of the layout flow but still in the DOM so Chart.js can measure it.
  canvas.style.position = 'fixed';
  canvas.style.left = '-99999px';
  canvas.style.top = '0';
  canvas.style.pointerEvents = 'none';
  document.body.appendChild(canvas);
  return canvas;
}

function renderChartToDataUrl(config: ChartConfiguration, canvas: HTMLCanvasElement): string {
  let chart: Chart | null = null;
  try {
    chart = new Chart(canvas, {
      ...config,
      plugins: [...(config.plugins ?? []), whiteBackgroundPlugin],
    });
    // Force a synchronous paint so toBase64Image captures pixels.
    chart.update('none');
    // Export as JPEG (no alpha channel) so @react-pdf/renderer doesn't run its
    // PNG alpha-splitting path, which spins up a blob:-URL Web Worker that
    // host-app CSPs (e.g. MyYoast) typically block.
    return chart.toBase64Image('image/jpeg', 0.92);
  } finally {
    chart?.destroy();
    canvas.remove();
  }
}

function buildRadarConfig(scan: Scan): ChartConfiguration<'radar'> {
  const std = scan.scores?.standardized;
  const mentionsPct = std?.mentions?.value ?? 0;
  const rankingPct = std?.ranking?.value ?? 0;
  const citationsPct = std?.citations?.value ?? 0;
  const sentimentPct = std?.sentiment?.value ?? 0;

  const data: ChartData<'radar'> = {
    labels: ['Mentions', 'Ranking', 'Citations', 'Sentiment'],
    datasets: [
      {
        data: [
          Number(mentionsPct),
          Number(rankingPct),
          Number(citationsPct),
          Number(sentimentPct),
        ],
        backgroundColor: PRIMARY_FILL,
        borderColor: PRIMARY,
        borderWidth: 2,
        pointBackgroundColor: PRIMARY,
        pointBorderColor: 'rgba(255,255,255,0.95)',
        pointBorderWidth: 2,
        pointRadius: 5,
      },
    ],
  };

  return {
    type: 'radar',
    data,
    options: {
      responsive: false,
      animation: false,
      maintainAspectRatio: true,
      devicePixelRatio: 2,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        r: {
          beginAtZero: true,
          min: 0,
          max: 100,
          ticks: {
            stepSize: 20,
            font: { size: 14 },
            color: '#64748b',
            backdropColor: 'transparent',
          },
          pointLabels: {
            font: { size: 18, weight: 'bold' },
            color: '#1e293b',
          },
          grid: { color: '#e2e8f0' },
          angleLines: { color: '#cbd5e1' },
        },
      },
    },
  };
}

function formatLineLabel(label: string | number): string {
  const date = new Date(label);
  if (Number.isNaN(date.getTime())) return String(label);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

export function buildLineConfig(scan: Scan): ChartConfiguration<'line'> | null {
  const chartData = scan.mentions?.chart;
  if (!chartData) return null;

  const labelsRaw: unknown[] = Array.isArray(chartData.labels)
    ? chartData.labels
    : chartData.labels && typeof chartData.labels === 'object'
      ? Object.values(chartData.labels as Record<string, unknown>)
      : [];

  const formattedLabels = labelsRaw.map((label) => formatLineLabel(String(label)));

  const datasets = (chartData.datasets ?? []).map(
    (ds): ChartDataset<'line', number[]> => ({
      label: ds.label,
      data: (ds.data ?? []) as number[],
      borderColor: PRIMARY,
      backgroundColor: PRIMARY_FILL,
      borderWidth: 3,
      pointBackgroundColor: PRIMARY,
      pointBorderColor: 'rgba(255,255,255,0.95)',
      pointBorderWidth: 2,
      pointRadius: 5,
      fill: true,
      tension: 0.25,
    }),
  );

  const firstDataset = datasets[0];
  if (!firstDataset || firstDataset.data.length < 2) return null;

  let maxYAxis = scan.numberOfQueries;
  if (!maxYAxis) {
    let largest = 0;
    datasets.forEach((ds) => {
      ds.data.forEach((value) => {
        if (typeof value === 'number' && value > largest) largest = value;
      });
    });
    maxYAxis = largest + 2;
  }

  return {
    type: 'line',
    data: { labels: formattedLabels, datasets },
    options: {
      responsive: false,
      animation: false,
      maintainAspectRatio: false,
      devicePixelRatio: 2,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 14 }, color: '#475569' },
        },
        y: {
          beginAtZero: true,
          max: maxYAxis,
          ticks: { stepSize: 1, font: { size: 14 }, color: '#475569' },
          grid: { color: '#e2e8f0' },
        },
      },
    },
  };
}

export interface PdfChartImages {
  radar: string;
  mentionsProgress: string | null;
}

/**
 * Renders the brand-analysis charts off-screen with print-tuned options
 * and returns JPEG data URLs suitable for embedding via @react-pdf/renderer's <Image />.
 *
 * Charts are intentionally rendered fresh (not screenshotted from the DOM) so the
 * exported images are sized for paged print, with larger fonts and no animation.
 */
export async function renderChartImagesForPdf(scan: Scan): Promise<PdfChartImages> {
  const radarCanvas = createOffscreenCanvas(RADAR_PX, RADAR_PX);
  const radar = renderChartToDataUrl(buildRadarConfig(scan), radarCanvas);

  const lineConfig = buildLineConfig(scan);
  let mentionsProgress: string | null = null;
  if (lineConfig) {
    const lineCanvas = createOffscreenCanvas(LINE_W, LINE_H);
    mentionsProgress = renderChartToDataUrl(lineConfig, lineCanvas);
  }

  return { radar, mentionsProgress };
}
